import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, Fingerprint, Trash2, Loader2, Plus, ShieldCheck, KeyRound, Laptop2 } from "lucide-react";
import {
  pinStatus, setPin as apiSetPin, removePin as apiRemovePin,
  listPasskeys, passkeyRegisterStart, passkeyRegisterFinish, deletePasskey,
  listTrustedDevices, revokeTrustedDevice,
} from "@/lib/api";
import { getDeviceId } from "@/lib/device";
import { toast } from "sonner";

// ------------ WebAuthn helpers (base64url ⇄ Uint8Array) ------------
const b64urlDecode = (s) => {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const b64 = (s + pad).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
};
const b64urlEncode = (buf) => {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/=+$/, "").replace(/\+/g, "-").replace(/\//g, "_");
};

const isWebAuthnSupported =
  typeof window !== "undefined" &&
  !!window.PublicKeyCredential &&
  typeof navigator.credentials?.create === "function";

/**
 * Security card for the Dashboard / Settings. Two independent sub-features:
 *   1. PIN lock (4-6 digits) — server-side hash, verified at app start + on 10min idle
 *   2. Passkeys — WebAuthn registration so the user can sign in with FaceID/TouchID
 *      on this specific device
 */
export const SecurityCard = () => {
  const [pinInfo, setPinInfo] = useState(null);   // {enabled, ...}
  const [passkeys, setPasskeys] = useState([]);
  const [trustedDevices, setTrustedDevices] = useState([]);

  // PIN dialog state
  const [pinOpen, setPinOpen] = useState(false);
  const [pinNew, setPinNew] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [pinCurrentPw, setPinCurrentPw] = useState("");
  const [pinSaving, setPinSaving] = useState(false);
  const [pinError, setPinError] = useState("");

  const [regBusy, setRegBusy] = useState(false);
  const [pinRemoving, setPinRemoving] = useState(false);

  const refresh = () => {
    pinStatus().then(setPinInfo).catch(() => setPinInfo({ enabled: false }));
    listPasskeys().then((r) => setPasskeys(r.passkeys || [])).catch(() => setPasskeys([]));
    listTrustedDevices().then((r) => setTrustedDevices(r.devices || [])).catch(() => setTrustedDevices([]));
  };
  useEffect(refresh, []);

  const revokeDevice = async (device_id) => {
    if (!window.confirm("Revoke this trusted device? The PIN prompt will show up on next open.")) return;
    try {
      await revokeTrustedDevice(device_id);
      // If we just revoked the CURRENT browser's trust, force PinGate to re-lock
      // on the very next mount — otherwise the user has to open a new tab to see
      // the prompt again. sessionStorage flag is the guard PinGate reads.
      if (device_id === getDeviceId()) {
        try { sessionStorage.removeItem("lectura_pin_ok"); } catch {}
        window.location.reload();
        return;
      }
      toast.success("Trusted device revoked");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't revoke");
    }
  };

  const submitPin = async (e) => {
    e?.preventDefault?.();
    setPinError("");
    if (!/^\d{4,6}$/.test(pinNew)) { setPinError("PIN must be 4-6 digits."); return; }
    if (pinNew !== pinConfirm) { setPinError("PINs don't match."); return; }
    if (pinInfo?.enabled && !pinCurrentPw) { setPinError("Enter your current password to rotate the PIN."); return; }
    setPinSaving(true);
    try {
      await apiSetPin(pinNew, pinCurrentPw);
      toast.success(pinInfo?.enabled ? "PIN updated" : "PIN set");
      setPinOpen(false);
      setPinNew(""); setPinConfirm(""); setPinCurrentPw("");
      refresh();
    } catch (err) {
      setPinError(err?.response?.data?.detail || "Couldn't save PIN.");
    } finally {
      setPinSaving(false);
    }
  };

  const removePin = async () => {
    const pw = window.prompt("Enter your account password to remove the PIN:");
    if (!pw) return;
    setPinRemoving(true);
    try {
      await apiRemovePin(pw);
      toast.success("PIN removed");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove PIN.");
    } finally {
      setPinRemoving(false);
    }
  };

  const registerPasskey = async () => {
    if (!isWebAuthnSupported) {
      toast.error("Your browser doesn't support passkeys. Try the latest Chrome, Safari or Edge.");
      return;
    }
    setRegBusy(true);
    try {
      const start = await passkeyRegisterStart();
      // Minimal PublicKeyCredentialCreationOptions for a passkey / platform authenticator
      const opts = {
        publicKey: {
          challenge: b64urlDecode(start.challenge),
          rp: { name: start.rp_name },
          user: {
            id: b64urlDecode(start.user_handle),
            name: start.user_name,
            displayName: start.user_display_name,
          },
          pubKeyCredParams: [
            { type: "public-key", alg: -7 },    // ES256
            { type: "public-key", alg: -257 },  // RS256
          ],
          authenticatorSelection: {
            residentKey: "preferred",
            userVerification: "preferred",
            authenticatorAttachment: "platform", // FaceID / TouchID / Windows Hello
          },
          timeout: 60_000,
          attestation: "none",
        },
      };
      const cred = await navigator.credentials.create(opts);
      if (!cred) throw new Error("Cancelled");
      const label = /iPhone|iPad|iOS/.test(navigator.userAgent) ? "iPhone / iPad"
                  : /Mac/.test(navigator.userAgent) ? "Mac (Touch ID)"
                  : /Windows/.test(navigator.userAgent) ? "Windows Hello"
                  : /Android/.test(navigator.userAgent) ? "Android"
                  : "This device";
      const pkResp = cred.response?.getPublicKey?.();
      await passkeyRegisterFinish({
        credential_id: b64urlEncode(cred.rawId),
        public_key: pkResp ? b64urlEncode(pkResp) : "",
        label,
      });
      toast.success(`Passkey saved · ${label}`);
      refresh();
    } catch (err) {
      const msg = err?.name === "NotAllowedError"
        ? "Cancelled — no changes made."
        : (err?.response?.data?.detail || err?.message || "Couldn't register passkey.");
      toast.error(msg);
    } finally {
      setRegBusy(false);
    }
  };

  const removePasskey = async (id) => {
    if (!window.confirm("Revoke this passkey? You'll still be able to sign in with your password.")) return;
    try {
      await deletePasskey(id);
      toast.success("Passkey removed");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove passkey.");
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-sm" data-testid="security-card">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="h-5 w-5 text-accent" />
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Security</span>
      </div>
      <h3 className="font-serif text-2xl font-bold">Lock & sign-in</h3>
      <p className="mt-1 text-sm text-muted-foreground">Protect your account on shared devices, and skip the password with a passkey.</p>

      {/* ----------- PIN ROW ----------- */}
      <div className="mt-5 rounded-2xl border border-border p-4 flex items-start justify-between gap-4" data-testid="security-pin-row">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-accent" />
            <p className="font-serif font-bold">App PIN</p>
            {pinInfo?.enabled && (
              <span className="rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">On</span>
            )}
          </div>
          <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
            4-6 digit PIN required to open Lectura on this device. Auto-re-locks after 10 min of inactivity — great for shared classroom iPads.
          </p>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <button
            data-testid="pin-open-btn"
            onClick={() => setPinOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-foreground px-4 py-2 text-xs font-semibold hover:opacity-90 transition"
          >
            <Lock className="h-3.5 w-3.5" /> {pinInfo?.enabled ? "Change PIN" : "Set PIN"}
          </button>
          {pinInfo?.enabled && (
            <button
              data-testid="pin-remove-btn"
              onClick={removePin}
              disabled={pinRemoving}
              className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-xs font-semibold hover:border-destructive/50 hover:text-destructive transition disabled:opacity-60"
            >
              {pinRemoving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Remove
            </button>
          )}
        </div>
      </div>

      {/* ----------- PASSKEYS ROW ----------- */}
      <div className="mt-3 rounded-2xl border border-border p-4" data-testid="security-passkeys-row">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Fingerprint className="h-4 w-4 text-accent" />
              <p className="font-serif font-bold">Passkeys / Biometrics</p>
              <span className="rounded-full bg-primary/10 text-primary text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">Beta</span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
              Sign in with Face ID, Touch ID, Windows Hello or a hardware key — no password to type. Registered per-device.
            </p>
            {!isWebAuthnSupported && (
              <p className="mt-2 text-[11px] text-amber-700 dark:text-amber-400">
                Your browser doesn't expose passkey APIs — try the latest Chrome, Safari or Edge.
              </p>
            )}
          </div>
          <button
            data-testid="passkey-register-btn"
            onClick={registerPasskey}
            disabled={regBusy || !isWebAuthnSupported}
            className="inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-foreground px-4 py-2 text-xs font-semibold disabled:opacity-60 hover:opacity-90 transition shrink-0"
          >
            {regBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add this device
          </button>
        </div>

        {passkeys.length > 0 && (
          <ul className="mt-4 divide-y divide-border border border-border rounded-xl overflow-hidden" data-testid="passkey-list">
            {passkeys.map((pk) => (
              <li key={pk.id} className="flex items-center gap-3 px-3 py-2.5 text-sm">
                <KeyRound className="h-3.5 w-3.5 text-accent shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{pk.label}</p>
                  <p className="text-[10px] text-muted-foreground">
                    Added {new Date(pk.created_at).toLocaleDateString()}
                    {pk.last_used_at ? ` · last used ${new Date(pk.last_used_at).toLocaleDateString()}` : " · never used"}
                  </p>
                </div>
                <button
                  data-testid={`passkey-delete-${pk.id}`}
                  onClick={() => removePasskey(pk.id)}
                  className="p-1.5 text-muted-foreground hover:text-destructive transition"
                  title="Revoke passkey"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ----------- TRUSTED DEVICES ROW ----------- */}
      {pinInfo?.enabled && (
        <div className="mt-3 rounded-2xl border border-border p-4" data-testid="security-trusted-devices-row">
          <div className="flex items-center gap-2">
            <Laptop2 className="h-4 w-4 text-accent" />
            <p className="font-serif font-bold">Trusted devices</p>
            <span className="rounded-full bg-secondary text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
              {trustedDevices.length} active
            </span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
            These browsers can open Lectura for 30 days without the PIN prompt. Revoke any you don't recognise.
          </p>
          {trustedDevices.length === 0 ? (
            <p className="mt-3 text-[11px] text-muted-foreground italic">
              None yet — check "Trust this device for 30 days" on the PIN unlock screen to add one.
            </p>
          ) : (
            <ul className="mt-3 divide-y divide-border border border-border rounded-xl overflow-hidden" data-testid="trusted-devices-list">
              {trustedDevices.map((d) => (
                <li key={d.device_id} className="flex items-center gap-3 px-3 py-2.5 text-sm" data-testid={`trusted-device-${d.device_id}`}>
                  <Laptop2 className="h-3.5 w-3.5 text-accent shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{d.label}</p>
                    <p className="text-[10px] text-muted-foreground">
                      Added {new Date(d.created_at).toLocaleDateString()} · expires {new Date(d.expires_at).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    data-testid={`trusted-device-revoke-${d.device_id}`}
                    onClick={() => revokeDevice(d.device_id)}
                    className="p-1.5 text-muted-foreground hover:text-destructive transition"
                    title="Revoke trusted device"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ----------- PIN DIALOG ----------- */}
      {pinOpen && (
        <div onClick={() => setPinOpen(false)} className="fixed inset-0 z-50 grid place-items-center bg-black/50 backdrop-blur-sm p-4">
          <motion.form
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitPin}
            className="w-full max-w-sm rounded-2xl bg-card border border-border shadow-2xl overflow-hidden"
            data-testid="pin-dialog"
          >
            <div className="p-5 border-b border-border">
              <p className="font-serif text-xl font-bold">{pinInfo?.enabled ? "Change your PIN" : "Set an app PIN"}</p>
              <p className="mt-1 text-xs text-muted-foreground">4-6 digits. Kids can't peek if a device is unlocked.</p>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">New PIN</label>
                <input
                  data-testid="pin-new-input"
                  type="password"
                  inputMode="numeric"
                  autoFocus
                  maxLength={6}
                  value={pinNew}
                  onChange={(e) => setPinNew(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="••••"
                  className="mt-1 w-full text-center tracking-[0.5em] font-mono text-2xl rounded-xl border border-input bg-background py-3 outline-none focus:ring-2 focus:ring-ring/60"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Confirm PIN</label>
                <input
                  data-testid="pin-confirm-input"
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  value={pinConfirm}
                  onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="••••"
                  className="mt-1 w-full text-center tracking-[0.5em] font-mono text-2xl rounded-xl border border-input bg-background py-3 outline-none focus:ring-2 focus:ring-ring/60"
                />
              </div>
              {pinInfo?.enabled && (
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Account password</label>
                  <input
                    data-testid="pin-current-pw"
                    type="password"
                    value={pinCurrentPw}
                    onChange={(e) => setPinCurrentPw(e.target.value)}
                    placeholder="Required to rotate the PIN"
                    className="mt-1 w-full rounded-xl border border-input bg-background px-4 py-2.5 outline-none focus:ring-2 focus:ring-ring/60"
                  />
                </div>
              )}
              {pinError && <p data-testid="pin-dialog-error" className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">{pinError}</p>}
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => setPinOpen(false)} className="flex-1 rounded-full border border-border py-2.5 text-sm font-semibold hover:border-primary/40 transition">Cancel</button>
                <button
                  type="submit"
                  data-testid="pin-save-btn"
                  disabled={pinSaving}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground py-2.5 text-sm font-semibold disabled:opacity-60 hover:opacity-90 transition"
                >
                  {pinSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save PIN"}
                </button>
              </div>
            </div>
          </motion.form>
        </div>
      )}
    </div>
  );
};
